import { apiGet, apiPost, apiPatch, apiProjectsGet, apiProjectsPost, apiProjectsPatch, apiProjectsDelete, apiRecordsGet, apiRecordsPost } from '../../utilities/apiClient';
import { generateMnemonic } from '../../utilities/generateMnemonic';
import { deriveSmkForNewSnapshot, bytesToHex } from '../../utilities/snapshotCrypto';
import { getSessionIds, getStoreState } from './_sessionIds';
import { recorder } from '../../services/sessionTelemetry/recorder';
import config from '../../config';
import debug from '../../utilities/debug';
import { conversationSummaryFrom } from '../../utilities/conversationSummary';
import {
  ADD_PROJECTS,
  ADD_PROJECT,
  ADD_QUERIES,
  ADD_CONVERSATION,
  SET_ACTIVE_CONVERSATION,
  SET_CONVERSATION_SUMMARY,
  FETCHING_PROJECTS,
  FETCHED_PROJECTS,
  FETCHING_QUERIES,
  FETCHED_QUERIES,
  UPDATE_PROJECT,
  REMOVE_PROJECT,
} from '../constants';


export const markSOSSeen = (
  projectId: string | number,
  eventId: string,
) => async (): Promise<{ success: boolean; payload?: { seen_by: Array<{ cognitoId: string; name: string; seenAt: string }> } }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPost(`/emergency-sos-ack/${projectId}/${accountId}/${cognitoId}/${eventId}`,
      {},
    );
    return response;
  } catch {
    return { success: false };
  }
};

export const fetchProjects = (accountId: string, cognitoId: string) => async (dispatch: any) => {
  const initialState = getStoreState();
  const initialUserId = initialState?.authentication?.user?.cognito_id;
  const startedAuthorized = initialState?.session?.authorized;
  const isCurrentScope = () => {
    const current = getStoreState();
    const currentUserId = current?.authentication?.user?.cognito_id;
    return current?.session?.account?.account_id === accountId
      && (currentUserId === cognitoId || (!initialUserId && !currentUserId))
      && (startedAuthorized !== true || current?.session?.authorized === true);
  };
  // Bootstrap loads projects before IS_LOGGED_IN and may continue after a
  // failed user fetch. Keep that path, while rejecting old account/user
  // callbacks and responses before they can replace the shared plan list.
  if (!accountId || !cognitoId || !isCurrentScope()) return { success: false, cancelled: true };
  dispatch({ type: FETCHING_PROJECTS });
  try {
    const response = await apiProjectsGet(`/get-all/${accountId}/${cognitoId}`);
    if (!isCurrentScope()) return { success: false, cancelled: true };
    if (response?.success) {
      dispatch({ type: ADD_PROJECTS, payload: response.payload || [] });
      dispatch({ type: FETCHED_PROJECTS });
      return { success: true, payload: response.payload };
    }
    dispatch({ type: FETCHED_PROJECTS });
    return { success: false };
  } catch (err: any) {
    if (!isCurrentScope()) return { success: false, cancelled: true };
    dispatch({ type: FETCHED_PROJECTS });
    return { success: false, message: err?.message };
  }
};

export const fetchQueries = (projectId: number | string, override = false) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  const pid = String(projectId);

  const state = getStoreState();
  const already = state.prompts?.queries?.fetched?.[pid];
  if (already && !override) return { success: true };

  dispatch({ type: FETCHING_QUERIES });
  try {
    const response = await apiGet(`/prompts/get-queries/${pid}/${accountId}/${cognitoId}`);
    if (response?.success) {
      dispatch({ type: ADD_QUERIES, payload: { project_id: pid, queries: response.payload || [] } });
      dispatch({ type: FETCHED_QUERIES });
      return { success: true, payload: response.payload };
    }
    dispatch({ type: FETCHED_QUERIES });
    return { success: false };
  } catch (err: any) {
    dispatch({ type: FETCHED_QUERIES });
    return { success: false, message: err?.message };
  }
};

export const fetchConversation = (conversationId: string, projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const expectedProjectId = encodeURIComponent(String(projectId));
    const response = await apiGet(
      `/prompts/get-conversation/${conversationId}/${accountId}/${cognitoId}?project_id=${expectedProjectId}`,
    );
    const queries = Array.isArray(response?.payload) ? response.payload : [];
    const matchesExpectedScope = queries.length > 0 && queries.every((query: any) => (
      String(query?.project_id) === String(projectId)
      && String(query?.account_id) === String(accountId)
    ));
    if (response?.success && matchesExpectedScope) {
      dispatch({
        type: ADD_CONVERSATION,
        payload: { project_id: String(projectId), queries },
      });
      dispatch({
        type: SET_CONVERSATION_SUMMARY,
        payload: { conversation_id: conversationId, summary: conversationSummaryFrom(response.summary) },
      });
      return { success: true, payload: queries };
    }
    return { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/**
 * Make a plan conversation the one Ellie's chat shows, fetched fresh so a turn
 * written outside the chat (a doctor's emailed reply, backend
 * services/email/reply-in-chat) is in it. A push or Ellie home's opener then
 * lands on that conversation: ChatScreen restores its active conversation once
 * the plan's saved turns hold it.
 */
export const openPlanConversation = (conversationId: string, projectId: number | string) => (dispatch: any) => {
  dispatch({ type: SET_ACTIVE_CONVERSATION, payload: conversationId });
  return dispatch(fetchConversation(conversationId, projectId));
};


// ─── Update Query (rating, etc.) ─────────────────────────────────────────────

export const updateQuery = (
  projectId: number | string,
  queryId: number | string,
  updates: Record<string, any>,
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiPatch(
      `/prompts/update-query/${projectId}/${queryId}/${accountId}/${cognitoId}`,
      { updates },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Voice message audio ─────────────────────────────────────────────────────

export interface VoiceMessageAudio {
  /** Playable URL; its short-lived token stands in for the auth header. */
  uri: string;
  /** Local time (ms) after which to fetch a new URL rather than play this one. */
  staleAt: number;
}

// Refresh this long before the token's expiry, so playback that starts at the
// last moment still loads inside the token's lifetime.
const VOICE_AUDIO_REFRESH_MARGIN_SECONDS = 30;

/**
 * A playback URL for the user's recording on one chat turn. The audio route is
 * public because expo-audio's native players and the web <audio> element
 * cannot send an Authorization header. It takes a token minted here instead,
 * after the backend checks this user's account and plan. Resolves to null when
 * there is nothing this user may play.
 */
export const fetchVoiceMessageAudio = (
  queryId: number | string,
) => async (_dispatch: any): Promise<VoiceMessageAudio | null> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiGet(`/prompts/get-query-audio-url/${queryId}/${accountId}/${cognitoId}?kind=input`);
    const token = response?.payload?.token;
    const expiresIn = Number(response?.payload?.expires_in);
    if (!response?.success || typeof token !== 'string' || !(expiresIn > 0)) return null;
    return {
      uri: `${config.apiGateway.endpoint}/prompts/get-query-audio/${queryId}/${accountId}/${cognitoId}?kind=input&token=${encodeURIComponent(token)}`,
      // Counted on this device's clock from receipt: clock skew against the
      // server cannot make an expired URL look playable.
      staleAt: Date.now() + Math.max(0, expiresIn - VOICE_AUDIO_REFRESH_MARGIN_SECONDS) * 1000,
    };
  } catch {
    return null;
  }
};

// ─── Update Project ───────────────────────────────────────────────────────────

export const updateProject = (
  projectId: number | string,
  updates: Record<string, any>,
  /** `analyze`: also look through the plan now (a patient-agent run). */
  options: { analyze?: boolean } = {},
) => async (dispatch: any, getState: () => any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPatch(`/update/${projectId}/${accountId}/${cognitoId}`,
      options.analyze ? { updates, analyze: true } : { updates },
    );
    if (response?.success && response.payload) {
      // The raw DB response has `users` as a plain array of cognito ID strings.
      // The projects list endpoint enriches `users` via a JOIN (adding cognito_id,
      // project_permissions, etc.). Preserve the existing enriched users so that
      // permission checks (e.g. watch carousel) continue to work after an update.
      const existingProject = getState()?.projects?.list?.[String(projectId)];
      const enrichedUsers = existingProject?.users;
      const payload = enrichedUsers
        ? { ...response.payload, users: enrichedUsers }
        : response.payload;
      dispatch({ type: ADD_PROJECT, payload });
    }
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Remove Project ──────────────────────────────────────────────────────────

/**
 * The key that encrypts a plan's deletion archive.
 *
 * Identical derivation to a backup — HKDF over the account root key with the
 * plan's snapshot salt — so restoring a deleted plan needs the same 12-word
 * account recovery code and nothing else. The server never sees the root key,
 * only this per-plan key, once.
 *
 * Returns a reason instead of throwing: a plan whose key cannot be derived can
 * still be deleted, it just cannot be brought back, and the screen has to be
 * able to say which of those two things is about to happen.
 */
export const deriveArchiveKeyHex = async (
  projectId: number | string,
): Promise<{ keyHex?: string; reason?: 'not_authenticated' | 'no_salt' | 'no_root_key' }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { reason: 'not_authenticated' };
  // Get-or-create, so a plan that was never backed up still gets a salt here
  // rather than being silently unarchivable.
  const saltRes = await apiRecordsGet(`/snapshots/salt/${projectId}/${accountId}/${cognitoId}`);
  const saltHex: string | undefined = saltRes?.payload?.salt;
  if (!saltHex) return { reason: 'no_salt' };
  // Null when the account root key is not on this device — the user has never
  // backed anything up, or is on a new phone and has not entered their code.
  const smk = await deriveSmkForNewSnapshot(accountId, projectId, saltHex);
  if (!smk) return { reason: 'no_root_key' };
  return { keyHex: bytesToHex(smk) };
};

export const removeProject = (
  projectId: number | string,
  opts?: { encryptionKeyHex?: string; retentionTerm?: string; exportPlan?: boolean },
) => async (dispatch: any): Promise<{
  success: boolean; message?: string; status_code?: string; archived?: boolean; exported?: boolean;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    // No key means no archive: the plan is erased permanently. That is a valid
    // choice the user makes on the delete screen, so it is not an error here.
    //
    // The two copies are independent — a plan can be archived, exported, both
    // or neither — so the body carries whichever was asked for and is omitted
    // entirely when neither was. export_plan is sent only as a literal true;
    // the server treats anything else as not asked for.
    const body: Record<string, unknown> = {};
    if (opts?.encryptionKeyHex) {
      body.encryption_key_hex = opts.encryptionKeyHex;
      body.retention_term = opts.retentionTerm || '6y';
    }
    if (opts?.exportPlan) body.export_plan = true;
    const response = await apiProjectsDelete(
      `/delete/${projectId}/${accountId}/${cognitoId}`,
      Object.keys(body).length ? body : undefined,
    );
    if (response?.success) {
      dispatch({ type: REMOVE_PROJECT, payload: projectId });
    }
    return response ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not delete this plan.' };
  }
};

/**
 * Fetch the project's past inbox addresses (distinct prior mnemonics
 * across `versioned` rows). Mail to those addresses still routes to the
 * project — this endpoint exists so the UI can show a caregiver which
 * old address they may have handed out before changing it.
 *
 * Returns an array of `{ mnemonic, valid_until }`, newest-first. The
 * currently-active mnemonic is excluded server-side. Not cached in
 * Redux — callers (one screen each, low-volume) keep this in local
 * state and refetch on focus.
 */
export interface InboxAlias {
  mnemonic: string;
  valid_until: string | null;
}
export const fetchProjectInboxAliases = async (
  projectId: number | string,
): Promise<InboxAlias[]> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return [];
  try {
    const response = await apiProjectsGet(
      `/${projectId}/${accountId}/${cognitoId}/inbox-aliases`,
    );
    if (response?.success && Array.isArray(response.payload)) {
      return response.payload as InboxAlias[];
    }
    return [];
  } catch {
    return [];
  }
};

/**
 * Enable (or disable) survey check-in questions for a project.
 *
 * 1. PATCHes the project blob with the provided settings.
 * 2. If enabling (method !== 'none'), calls the schedule-questions endpoint
 *    to kick off the self-managing question chain immediately.
 */
export const enableProjectQuestions = (
  projectId: number | string,
  settings: {
    prompts_method: 'sms' | 'push' | 'email' | 'none';
    prompts_frequency?: string;
    prompts_time?: string;
    adaptive_cadence?: boolean;
    remind?: boolean;
    prompts_chat_enabled?: boolean;
  },
) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();

  // 1 — Persist the settings to the project blob
  const updateResp = await dispatch(updateProject(projectId, { blob: settings }));
  if (!updateResp?.success) {
    return { ...updateResp, success: false, settingsSaved: false };
  }

  // 2 — Start (or cancel) the question chain
  let scheduleResult;
  try {
    scheduleResult = await apiProjectsPost(`/schedule-questions/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
  } catch (err: any) {
    scheduleResult = { success: false, message: err?.message };
  }

  if (settings.prompts_method === 'none' && scheduleResult?.success !== true) {
    return {
      success: false,
      settingsSaved: true,
      cancellationSucceeded: false,
      scheduleResult,
      message: scheduleResult?.message || 'Could not cancel pending check-ins.',
    };
  }

  // A cadence hold or a sparse plan with no eligible question is a normal
  // scheduler outcome, not a failed settings save. The recurring scheduler can
  // pick the chain up later, so callers should close setup once persistence
  // succeeds while retaining the scheduling result for diagnostics.
  return {
    success: true,
    settingsSaved: true,
    cancellationSucceeded: settings.prompts_method === 'none' ? true : undefined,
    scheduleResult,
  };
};

// ─── Create Project ───────────────────────────────────────────────────────────

export const createProject = (project: any) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  const projectWithMnemonic = project.mnemonic ? project : { ...project, mnemonic: generateMnemonic() };
  try {
    const response = await apiProjectsPost(`/create/${accountId}/${cognitoId}`, { project: projectWithMnemonic });
    if (response?.success) {
      dispatch({ type: ADD_PROJECT, payload: response.payload });
      recorder.track('plan_created');
      return { success: true, payload: response.payload };
    }
    return { success: false, message: response?.message || 'Could not create project.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not create project.' };
  }
};

// ─── Extract from text (Typeform Step 0 bulk-fill) ──────────────────────────
// One-shot AI extraction: free-form text in, partial field map + ready-to-
// save medication / contact arrays out. Used by the project-creation
// Typeform's "describe in your own words" affordance to pre-fill the form
// AND seed the new project with mentioned medications/contacts.
//
// Returns `{ success, extracted, medications, contacts, rationale, message? }`.
// All four data fields are always present:
//   - `extracted`:   object (possibly empty)
//   - `medications`: array  (possibly empty)
//   - `contacts`:    array  (possibly empty)
//   - `rationale`:   string (possibly empty)
// Caller merges `extracted` into survey-core state, surfaces `medications`
// and `contacts` in the review screen, and after createProject succeeds
// fans out `/medications/create` and `/contacts/create` POSTs for any rows
// the user kept.
export interface ExtractedMedicationShape {
  name: string;
  dose?: string;
  unit?: string;
  frequency?: string;
  physician?: string;
  notes?: string;
}
export interface ExtractedContactShape {
  first_name: string;
  last_name?: string;
  type?: string;
  email?: string;
  phone?: string;
  notes?: string;
}
export interface ExtractFromTextResult {
  success: boolean;
  extracted: Record<string, any>;
  medications: ExtractedMedicationShape[];
  contacts: ExtractedContactShape[];
  rationale: string;
  message?: string;
}
export async function extractPlanFieldsFromText(
  freeText: string,
  mode: 'client' | 'partner' = 'client',
): Promise<ExtractFromTextResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, extracted: {}, medications: [], contacts: [], rationale: '', message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/extract-from-text/${accountId}/${cognitoId}`,
      { free_text: freeText, mode },
    );
    if (response?.success && response.payload) {
      return {
        success: true,
        extracted: (response.payload.extracted && typeof response.payload.extracted === 'object')
          ? response.payload.extracted
          : {},
        medications: Array.isArray(response.payload.medications) ? response.payload.medications : [],
        contacts: Array.isArray(response.payload.contacts) ? response.payload.contacts : [],
        rationale: typeof response.payload.rationale === 'string' ? response.payload.rationale : '',
      };
    }
    return {
      success: false,
      extracted: {},
      medications: [],
      contacts: [],
      rationale: '',
      message: response?.message || 'Extraction failed.',
    };
  } catch (err: any) {
    return {
      success: false,
      extracted: {},
      medications: [],
      contacts: [],
      rationale: '',
      message: err?.message || 'Network error.',
    };
  }
}

// ─── Document-driven intake primitives ──────────────────────────────────────
//
// Three thin wrappers around the backend endpoints introduced in PR #853.
// Used by the Typeform "Upload a document" flow:
//
//   1. startIntake()                       → creates an intake_pending project
//   2. (existing) uploadDocument(...)      → file goes through standard processor
//   3. extractSurveyFieldsFromProject(pid) → post-process, pull survey fields
//   4. finalizeIntake(pid, project)        → flip status='active', patch fields
//
// The intake_pending row is invisible to /get-all and every other read until
// finalize flips it, so an abandoned mid-flow project doesn't surface
// anywhere user-visible.

export interface StartIntakeResult {
  success: boolean;
  project_id?: number | string | null;
  message?: string;
  errorCode?: string;
  limit?: number;
  current?: number;
  /** ED-2563 advisory: drafting is always allowed; at_cap means activation
   *  (finalize) will refuse until the user upgrades or frees a slot. */
  cap?: { at_cap: boolean; limit: number | null; current: number };
}
export async function startIntake(): Promise<StartIntakeResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/start-intake/${accountId}/${cognitoId}`,
      {},
    );
    if (response?.success && response.payload?.project_id != null) {
      return { success: true, project_id: response.payload.project_id, cap: response.payload.cap };
    }
    return {
      success: false,
      message: response?.message || 'Could not start intake.',
      errorCode: response?.errorCode,
      limit: response?.limit,
      current: response?.current,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

export interface ExtractSurveyFieldsFromProjectResult {
  success: boolean;
  extracted: Record<string, any>;
  rationale: string;
  message?: string;
}
export async function extractSurveyFieldsFromProject(
  projectId: number | string,
): Promise<ExtractSurveyFieldsFromProjectResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, extracted: {}, rationale: '', message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/extract-survey-fields-from-project/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
    if (response?.success && response.payload) {
      return {
        success: true,
        extracted: (response.payload.extracted && typeof response.payload.extracted === 'object')
          ? response.payload.extracted
          : {},
        rationale: typeof response.payload.rationale === 'string' ? response.payload.rationale : '',
      };
    }
    return {
      success: false,
      extracted: {},
      rationale: '',
      message: response?.message || 'Could not extract survey fields.',
    };
  } catch (err: any) {
    return {
      success: false,
      extracted: {},
      rationale: '',
      message: err?.message || 'Network error.',
    };
  }
}

// List the caller's outstanding intake_pending projects. Powers the
// Plans-screen resume cards. Each row carries the cached extraction +
// in-progress walker answers, plus the document and per-table counts
// the card needs to preview.
export interface IntakePendingRow {
  project_id: number | string;
  created_at: string;
  blob: {
    intake?: boolean;
    intake_extracted?: { extracted?: Record<string, any>; rationale?: string; extracted_at?: string };
    intake_progress?: { data?: Record<string, any>; updated_at?: string };
  };
  document: { id: number | string; filename: string; status: string; summary?: string | null } | null;
  medication_count: number;
  contact_count: number;
}
export interface FetchIntakePendingResult {
  success: boolean;
  rows: IntakePendingRow[];
  message?: string;
}
export async function fetchIntakePending(): Promise<FetchIntakePendingResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, rows: [], message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsGet(
      `/intake-pending/${accountId}/${cognitoId}`,
    );
    if (response?.success && Array.isArray(response.payload)) {
      return { success: true, rows: response.payload };
    }
    return { success: false, rows: [], message: response?.message || 'Could not list pending intakes.' };
  } catch (err: any) {
    return { success: false, rows: [], message: err?.message || 'Network error.' };
  }
}

// Persist partial walker answers on the intake row. Called debounced from
// the renderer after each answer commit. Failures are silently logged —
// the next save overwrites. We don't surface errors to the user because
// the typical failure mode is transient network and they're in the middle
// of a flow.
export async function saveIntakeProgress(
  projectId: number | string,
  data: Record<string, any>,
): Promise<{ success: boolean; message?: string }> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/save-intake-progress/${projectId}/${accountId}/${cognitoId}`,
      { data },
    );
    if (response?.success) return { success: true };
    return { success: false, message: response?.message || 'Save failed.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

export interface FinalizeIntakeResult {
  success: boolean;
  payload?: any;
  message?: string;
  /** PLANS_LIMIT_REACHED when activation was refused at the cap (ED-2563) —
   *  the draft shell is untouched and resumable. */
  errorCode?: string;
  limit?: number;
  current?: number;
}
export async function finalizeIntake(
  projectId: number | string,
  project: any,
): Promise<FinalizeIntakeResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/finalize-intake/${projectId}/${accountId}/${cognitoId}`,
      { project },
    );
    if (response?.success && response.payload) {
      return { success: true, payload: response.payload };
    }
    return {
      success: false,
      message: response?.message || 'Could not finalize intake.',
      errorCode: response?.errorCode,
      limit: response?.limit,
      current: response?.current,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

// ─── Survey configuration fetch (no version filter) ─────────────────────────
//
// Fetches a SurveyJS configuration by `survey_id` via the existing
// `/projects/get-json/{survey_id}/...` endpoint — which is unversioned by
// survey_id and DynamoDB-cached. The Typeform renderer needs a survey-by-id
// fetch that doesn't apply the global block_version filter (the project-
// creation survey is pinned to v1; the redux `state.blocks.list` slice is
// populated by `/blocks/get-all` which IS version-filtered, so a screen
// reading from it can see an empty list).
export interface FetchSurveyConfigResult {
  success: boolean;
  configuration: any | null;
  message?: string;
}
export async function fetchSurveyConfig(
  surveyId: string,
): Promise<FetchSurveyConfigResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, configuration: null, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsGet(
      `/get-json/${surveyId}/${accountId}/${cognitoId}`,
    );
    if (response?.success && response.payload?.configuration) {
      return { success: true, configuration: response.payload.configuration };
    }
    return {
      success: false,
      configuration: null,
      message: response?.message || 'Survey not found.',
    };
  } catch (err: any) {
    return {
      success: false,
      configuration: null,
      message: err?.message || 'Network error.',
    };
  }
}


/**
 * Optimistically updates a project's health_alerts_config in Redux, then
 * patches the backend. Rolls back on failure.
 */
export const updateHealthAlertsConfig = (
  projectId: string | number,
  config: Record<string, any>,
) => async (dispatch: any): Promise<{ success: boolean; message?: string; payload?: { health_alerts_config: any } }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };

  // Optimistic update.
  dispatch({ type: UPDATE_PROJECT, payload: { id: projectId, health_alerts_config: config } });

  try {
    const response = await apiProjectsPatch(`/update-health-alerts-config/${projectId}/${accountId}/${cognitoId}`,
      { config },
    );
    if (response?.success) {
      // Sync with server-confirmed config.
      dispatch({
        type: UPDATE_PROJECT,
        payload: { id: projectId, health_alerts_config: response.payload?.health_alerts_config },
      });
      return { success: true, payload: response.payload };
    }
    // Roll back optimistic update.
    const state = getStoreState() as any;
    const current = state.projects?.list?.[projectId]?.health_alerts_config;
    dispatch({ type: UPDATE_PROJECT, payload: { id: projectId, health_alerts_config: current } });
    return { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/**
 * The health alerts this plan has actually fired, newest first.
 *
 * Every alert writes a `health_event#` record, and until /get-health-events
 * existed nothing read one back — the push notification was the only copy, so
 * an alert vanished the moment it was dismissed. Not cached in Redux: one
 * screen reads it and refetches on focus, matching fetchProjectInboxAliases.
 *
 * `success` is distinguished from an empty list on purpose. "No alerts yet" and
 * "we could not load them" are different things to tell someone about their
 * own health history.
 */
export interface HealthAlertEvent {
  eventId: string;
  alertType: string | null;
  ruleId: string | null;
  ruleLabel: string | null;
  timestamp: string | null;
  notified: number;
  recipients: string | null;
  context: Record<string, any> | null;
}
export const fetchHealthAlertEvents = async (
  projectId: number | string,
): Promise<{ success: boolean; events: HealthAlertEvent[] }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, events: [] };
  try {
    const response = await apiProjectsGet(
      `/get-health-events/${projectId}/${accountId}/${cognitoId}`,
    );
    if (response?.success && Array.isArray(response.payload?.events)) {
      return { success: true, events: response.payload.events as HealthAlertEvent[] };
    }
    return { success: false, events: [] };
  } catch {
    return { success: false, events: [] };
  }
};

// ─── Diagnoses Catalog ────────────────────────────────────────────────────────
//
// Fetches the active diagnoses catalog rows and submits user-generated requests
// for new diagnoses. Server contract lives under /diagnoses/* (global catalog,
// not per-project). See backend/api/client/controllers/projects/list-diagnoses
// and request-diagnosis — named under `projects/` in the controller tree for
// domain grouping, but exposed at top-level paths to avoid colliding with the
// greedy /projects/{project_id}/... route.

export interface DiagnosisCatalogRow {
  id: number;
  code: string;
  label: string;
  category: string | null;
  synonyms: string[] | null;
  status: 'active' | 'archived';
  metadata?: Record<string, any> | null;
  updated_at?: string | null;
  created_at?: string | null;
}

export type DiagnosisCandidateStatus = 'pending' | 'approved' | 'rejected';
export type DiagnosisCandidateReviewDecision = 'approve' | 'reject';

export interface DiagnosisCandidateCoding {
  system: string;
  code: string;
  display?: string | null;
}

export interface DiagnosisCandidateEvidence {
  key?: string;
  kind: 'document' | 'note' | 'connected_record' | 'plan';
  source_id?: string | number | null;
  title?: string | null;
  excerpt?: string | null;
  observed_at?: string | null;
  record_id?: string | number | null;
  source_org?: string | null;
  effective_date?: string | null;
  raw_label?: string | null;
}

export interface DiagnosisCandidateRow {
  id?: number | string;
  candidate_id?: number | string;
  project_id?: number | string;
  materialized?: boolean;
  source?: 'extraction' | 'connected_record';
  identity_key?: string;
  label: string;
  catalog_code: string | null;
  codings: DiagnosisCandidateCoding[];
  evidence: DiagnosisCandidateEvidence[];
  confidence: number | null;
  assertion_status?: 'potential' | 'historical' | 'rejected';
  plan_status?: 'potential' | 'historical' | 'rejected';
  status: DiagnosisCandidateStatus;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  reviewer_notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DiagnosisCandidateReviewPayload {
  candidate: DiagnosisCandidateRow;
  project_updated?: boolean;
  project_diagnoses?: {
    diagnoses_select_all: string[];
    diagnoses_other: Array<{ diagnosis_name: string }>;
  } | null;
  project?: Record<string, any>;
}

export type CanonicalProjectDiagnoses = NonNullable<
  DiagnosisCandidateReviewPayload['project_diagnoses']
>;

/**
 * Apply the diagnosis arrays returned by the review transaction to the local
 * project cache. The review endpoint already committed this exact state; a
 * second project update would create an unnecessary version and a list refetch
 * can race a stale cache. Keeping the rest of the existing blob intact makes
 * Settings and a remounted diagnoses screen immediately agree with the server.
 */
export const applyCanonicalProjectDiagnoses = (
  projectId: number | string,
  diagnoses: CanonicalProjectDiagnoses,
) => (dispatch: any, getState: () => any) => {
  const existing = getState()?.projects?.list?.[String(projectId)];
  dispatch({
    type: ADD_PROJECT,
    payload: {
      id: existing?.id ?? projectId,
      blob: {
        ...(existing?.blob || {}),
        diagnoses_select_all: [...diagnoses.diagnoses_select_all],
        diagnoses_other: diagnoses.diagnoses_other.map((entry) => ({ ...entry })),
      },
    },
  });
};

type DiagnosisCandidatesEnvelope = {
  candidates?: DiagnosisCandidateRow[];
  diagnoses?: DiagnosisCandidateRow[];
  groups?: { potential?: Array<DiagnosisCandidateRow | number | string> };
  can_review?: boolean;
};

const normalizeCandidateRows = (rows: DiagnosisCandidateRow[]): DiagnosisCandidateRow[] => rows
  .filter((row) => row && typeof row === 'object' && typeof row.label === 'string')
  .map((row) => ({
    ...row,
    status: row.status || (row.plan_status === 'potential' ? 'pending' : row.plan_status),
    catalog_code: row.catalog_code || null,
    codings: Array.isArray(row.codings) ? row.codings : [],
    evidence: Array.isArray(row.evidence) ? row.evidence : [],
    confidence: typeof row.confidence === 'number' ? row.confidence : null,
  }))
  .filter((row) => row.status === 'pending'
    || row.status === 'approved'
    || row.status === 'rejected');

const candidateRowsFromPayload = (payload: unknown): DiagnosisCandidateRow[] => {
  if (Array.isArray(payload)) return normalizeCandidateRows(payload as DiagnosisCandidateRow[]);
  if (!payload || typeof payload !== 'object') return [];
  const envelope = payload as DiagnosisCandidatesEnvelope;
  if (Array.isArray(envelope.candidates)) return normalizeCandidateRows(envelope.candidates);
  if (!Array.isArray(envelope.diagnoses)) {
    const inlinePotential = Array.isArray(envelope.groups?.potential)
      ? envelope.groups.potential.filter((row): row is DiagnosisCandidateRow => (
          !!row && typeof row === 'object'
        ))
      : [];
    return normalizeCandidateRows(inlinePotential);
  }

  const potentialIds = new Set(
    (Array.isArray(envelope.groups?.potential) ? envelope.groups.potential : [])
      .filter((value): value is number | string => (
        typeof value === 'number' || typeof value === 'string'
      ))
      .map(String),
  );
  const potentialRows = envelope.diagnoses.filter((row) => (
    row?.plan_status === 'potential'
    || row?.status === 'pending'
    || (row?.id != null && potentialIds.has(String(row.id)))
    || (row?.candidate_id != null && potentialIds.has(String(row.candidate_id)))
  ));
  return normalizeCandidateRows(potentialRows);
};

export const listDiagnosisCandidates = async (
  projectId: number | string,
): Promise<{
  success: boolean;
  payload?: DiagnosisCandidateRow[];
  can_review?: boolean;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const response = await apiProjectsGet(
      `/diagnoses/candidates/${encodeURIComponent(String(projectId))}/${accountId}/${cognitoId}?status=pending`,
    );
    if (!response?.success) return response;
    const envelope = response.payload && typeof response.payload === 'object'
      ? response.payload as DiagnosisCandidatesEnvelope
      : null;
    return {
      ...response,
      payload: candidateRowsFromPayload(response.payload),
      can_review: envelope?.can_review,
    };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const reviewDiagnosisCandidate = async (
  projectId: number | string,
  candidateId: number | string,
  decision: DiagnosisCandidateReviewDecision,
  reviewerNotes?: string,
): Promise<{
  success: boolean;
  payload?: DiagnosisCandidateRow | DiagnosisCandidateReviewPayload;
  project?: Record<string, any>;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    return await apiProjectsPost(
      `/diagnoses/candidates/${encodeURIComponent(String(projectId))}/${accountId}/${cognitoId}`,
      {
        candidate_id: candidateId,
        decision,
        ...(reviewerNotes?.trim() ? { reviewer_notes: reviewerNotes.trim() } : {}),
      },
    );
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export interface RareDiseaseSearchRow {
  slug: string;
  orpha_code: string;
  name: string;
  also_called: string[];
  disorder_type?: string | null;
  summary?: string | null;
  matched_synonym?: string | null;
}

export const listDiagnosesCatalog = async (options: {
  /** Also the codes plans have most, in order (`popular_codes`). */
  rankByUsage?: boolean;
  /** With rankByUsage: among recipients of this age. */
  age?: number | null;
} = {}): Promise<{
  success: boolean;
  payload?: DiagnosisCatalogRow[];
  popular_codes?: string[];
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  const query = options.rankByUsage
    ? `?rank=usage${options.age != null && Number.isInteger(options.age) ? `&age=${options.age}` : ''}`
    : '';
  try {
    const response = await apiRecordsGet(`/diagnoses/list/${accountId}/${cognitoId}${query}`);
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const requestDiagnosis = async (
  label: string,
  projectId?: number | string | null,
): Promise<{
  success: boolean;
  deduped?: boolean;
  payload?: any;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiRecordsPost(
      `/diagnoses/request/${accountId}/${cognitoId}`,
      {
        label,
        project_id: projectId == null ? null : projectId,
      },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const searchRareDiseases = async (
  query: string,
): Promise<{
  success: boolean;
  payload?: RareDiseaseSearchRow[];
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  const q = String(query || '').trim();
  if (q.length < 2) return { success: true, payload: [] };
  try {
    return await apiRecordsGet(
      `/diagnoses/rare-diseases/${accountId}/${cognitoId}?q=${encodeURIComponent(q)}`,
    );
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const addRareDiseaseToCatalog = async (
  orphaCode: string,
): Promise<{
  success: boolean;
  payload?: DiagnosisCatalogRow;
  created?: boolean;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    return await apiRecordsPost(
      `/diagnoses/rare-diseases/${accountId}/${cognitoId}`,
      { orpha_code: orphaCode },
    );
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Transfer project ─────────────────────────────────────────────────────────
//
// Hand a plan over to another Eleplan account. The recipient must:
//   1. have an active Eleplan account,
//   2. not be a partner account,
//   3. supply the initiator with the plan's 6-digit `transfer_code`.
//
// Reuses the same endpoint that the web app calls (see
// backend/api/client/controllers/projects/transfer). The caller must have
// `owner` or `administrator` on the current account — the backend re-checks.
//
// On success the projects list is refetched so the transferred plan disappears
// locally; the backend has already inactivated the initiator's membership.

export const transferProject = (
  email: string,
  projectId: number | string,
  transferCode: string,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPost(`/transfer/${accountId}/${cognitoId}`,
      {
        email: String(email || '').trim().toLowerCase(),
        project_id: projectId,
        transfer_code: String(transferCode || '').trim(),
      },
    );
    if (response?.success) {
      // Refresh the projects list so the transferred plan is removed and any
      // pending-transfer flags show on membership rows.
      try { await dispatch(fetchProjects(accountId, cognitoId)); } catch {}
    }
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Project memberships ─────────────────────────────────────────────────────
//
// Add or remove team members on a project. Backed by the projects service's
// /create-membership and /delete-membership endpoints. Mirrors the legacy
// web client's signatures (memberships, project_id, full) and
// (membership_ids, project_id) so callers can drop them in unchanged.
//
// Cache invalidation is intentionally out of scope here — the partner screen
// callers (PartnerCustomerDetailScreen → ManageMembersSheet) own their own
// `onMembersChanged` / `onAfterChange` refresh hooks. Wiring fetchProjects /
// account-wide refetches in here would double-fetch on every save.

export interface MembershipCreateRow {
  project_id: number | string;
  account_id: string | number;
  cognito_id: string;
  // Optional permissions/role passthrough — backend tolerates unknown fields.
  [key: string]: any;
}

export interface MembershipDeleteRow {
  id: number | string;
  cognito_id: string;
  account_id: string | number;
}

export const createProjectMembership = (
  memberships: MembershipCreateRow[],
  _projectId?: number | string,
  full = false,
  // The account that OWNS the target project. For a partner assigning a
  // caseworker to a CUSTOMER's plan, the project lives on the customer's
  // account — create-membership must target that account, not the partner's
  // session account, or the backend's `WHERE project.account_id = <path>`
  // ownership check drops every row → "Could not create project memberships".
  accountIdOverride?: string,
) => async (): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId: sessionAccountId, cognitoId } = getSessionIds();
  const accountId = accountIdOverride || sessionAccountId;
  try {
    const response = await apiProjectsPost(
      `/create-membership/${accountId}/${cognitoId}`,
      { memberships, full },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not add team members.' };
  }
};

/**
 * Update the current user's own project_membership row on a single project.
 * Used for self-toggleable flags like email_forwarding_enabled. Hits the
 * existing update-account-user controller with target_cognito_id = self —
 * the controller already authorizes acting-as-self because it only checks
 * that the target is on the same account.
 */
export const updateOwnProjectMembership = (
  projectId: number | string,
  updates: Record<string, any>,
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const response = await apiPatch(
      `/users/update-account-user/${projectId}/${cognitoId}/${accountId}/${cognitoId}`,
      { updates: { project_membership: updates } },
    );
    return response ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Update failed' };
  }
};

/**
 * Save a plan person's own details (birthday, state, ZIP) on their member
 * record, the way the member form does, then reload the plans so the plan
 * reads them back.
 */
export const updatePlanPersonDetails = (
  projectId: number | string,
  personCognitoId: string,
  user: { birthday?: string; state?: string; zip?: string },
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const response = await apiPatch(
      `/users/update-account-user/${projectId}/${personCognitoId}/${accountId}/${cognitoId}`,
      { updates: { user } },
    );
    if (response?.success) await dispatch(fetchProjects(accountId, cognitoId));
    return response ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Update failed' };
  }
};

/**
 * Ask the plan's admins for permissions you don't have.
 *
 * The server records the ask on your own project_membership row and pushes
 * everyone who could approve it — account owners/administrators plus the
 * plan's `members` holders. Nothing is granted here; an admin still has to
 * approve, which is why this is safe to expose to every member.
 */
export const requestProjectPermissions = (
  projectId: number | string,
  permissions: string[],
  accountPermissions: string[] = [],
) => async (): Promise<{
  success: boolean; message?: string; requested?: string[]; accountRequested?: string[];
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  if (!permissions?.length && !accountPermissions?.length) return { success: false, message: 'Nothing selected' };
  try {
    const response = await apiPost(
      // `projectId` may be undefined for an account-only ask; the server
      // requires a real one only when plan permissions are in the payload.
      `/users/request-permissions/${projectId}/${accountId}/${cognitoId}`,
      { permissions, account_permissions: accountPermissions },
    );
    return {
      success: response?.success !== false,
      message: response?.message,
      requested: response?.payload?.requested_permissions,
      accountRequested: response?.payload?.account_requested_permissions,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not send your request.' };
  }
};

export const deleteProjectMembership = (
  membershipIds: MembershipDeleteRow[],
  projectId: number | string,
  // Same as createProjectMembership: pass the CUSTOMER's account when a partner
  // is managing a caseworker on a customer's plan, so the path targets the
  // account that owns the project rather than the partner's session account.
  accountIdOverride?: string,
) => async (): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId: sessionAccountId, cognitoId } = getSessionIds();
  const accountId = accountIdOverride || sessionAccountId;
  try {
    const response = await apiProjectsDelete(
      `/delete-membership/${projectId}/${accountId}/${cognitoId}`,
      { membership_ids: membershipIds },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not remove team members.' };
  }
};
